import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getBackendHost } from '@/lib/api-client';

interface User {
  id: string;
  email: string;
  name: string;
  groups?: Array<{ name: string; id: string; path: string }>;
}

interface AuthContextType {
  user: User | null;
  login: () => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    window.location.href = '/login';
  };

  // Set up axios interceptor for authentication and base URL
  useEffect(() => {
    const currentHost = window.location.hostname;
    const protocol = "https:";
    const port = window.location.port;

    const backendHost = getBackendHost(currentHost);
    const backendUrl = `${protocol}//${backendHost}/api/frontend`;

    axios.defaults.baseURL = backendUrl;

    console.log(`Global Admin Frontend running on: ${protocol}//${currentHost}${port ? ':' + port : ''}`);
    console.log(`Backend API URL: ${backendUrl}`);

    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const handleOAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const access_token = urlParams.get('access_token');
      const error = urlParams.get('error');

      if (error) {
        console.error('OAuth callback error:', error);
        return false;
      }

      if (success === 'true' && access_token) {
        localStorage.setItem('access_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        try {
          const userResponse = await axios.get('/users/me');
          setUser(userResponse.data);
        } catch (userError) {
          console.error('Failed to get user info:', userError);
          setUser({ id: 'unknown', email: 'unknown', name: 'User' });
        }

        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
      }

      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        const response = await axios.get(`/auth/keycloak-callback/?code=${code}&state=${state}`);
        const { access_token: token, user } = response.data;

        localStorage.setItem('access_token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
      }

      return false;
    } catch (error) {
      console.error('OAuth callback failed:', error);
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const access_token = urlParams.get('access_token');
      const error = urlParams.get('error');
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if ((success === 'true' && access_token) || (code && state) || error) {
        const callbackSuccess = await handleOAuthCallback();
        if (callbackSuccess) {
          setIsLoading(false);
          return;
        }
      }

      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axios.get('/users/me');
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('access_token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async () => {
    try {
      // Pass frontend_type=global-admin so the callback redirects back here
      const response = await axios.get('/auth/keycloak-init?frontend_type=global-admin');
      const { auth_url } = response.data;
      window.location.href = auth_url;
    } catch (error) {
      console.error('Failed to initiate Keycloak login:', error);
      throw new Error('Login failed');
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
